/**
 * Module dependencies.
 */

import { Checkbox } from 'app/components/ui/checkbox';
import { FeatureFlag, useFeatureFlags, useUpdateFeatureFlag } from 'app/hooks/requests/use-feature-flags';
import { Navigate } from 'react-router-dom';
import { Spinner } from 'app/components/ui/spinner';
import { Title } from 'app/components/atoms/title/title';
import { appRoutes } from 'app/routes';
import { useMe } from 'app/hooks/requests/use-me';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { useTranslation } from 'react-i18next';
import React from 'react';

/**
 * The order features are listed in.
 */

const order = [
  'nordigen',
  'edenred',
  'coverflex',
  'enableBanking',
  'cryptoManager',
  'cryptoGest',
  'notes',
  'projects',
  'poolInvest',
  'cryptoBot'
];

/**
 * Export `AdministrationFeatureFlags` component.
 *
 * Admin-only. Each feature has two independent switches — one for regular users
 * and one for admins. A person's effective access is `isAdmin ? admins : users`.
 */

export function AdministrationFeatureFlags(): JSX.Element {
  const [translate] = useTranslation();
  const { success, error } = useToast();
  const { data: me } = useMe();
  const { data: flags, isLoading } = useFeatureFlags();
  const updateFlag = useUpdateFeatureFlag();

  if (me && !me.isAdmin) {
    return <Navigate replace to={appRoutes.dashboard.home.base} />;
  }

  const sorted = [...(flags ?? [])].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));

  const toggle = async (flag: FeatureFlag, field: 'enabledForUsers' | 'enabledForAdmins', value: boolean) => {
    try {
      await updateFlag.mutateAsync({ key: flag.key, [field]: value });

      success({ message: translate('featureFlags.saved') });
    } catch {
      error({ message: translate('featureFlags.saveFailed') });
    }
  };

  return (
    <div className={'space-y-5'}>
      <div>
        <Title>{translate('featureFlags.title')}</Title>

        <p className={'mt-1 text-sm text-muted-foreground'}>{translate('featureFlags.description')}</p>
      </div>

      <div className={'overflow-hidden rounded-xl border border-border bg-card shadow-sm'}>
        {isLoading ? (
          <div className={'grid place-items-center py-16'}>
            <Spinner />
          </div>
        ) : sorted.length === 0 ? (
          <p className={'py-16 text-center text-sm text-muted-foreground'}>{translate('featureFlags.empty')}</p>
        ) : (
          <table className={'w-full text-sm'}>
            <thead>
              <tr className={'border-b border-border text-left text-muted-foreground'}>
                <th className={'px-4 py-3 font-medium'}>{translate('featureFlags.columns.feature')}</th>

                <th className={'w-[120px] px-4 py-3 text-center font-medium'}>
                  {translate('featureFlags.columns.users')}
                </th>

                <th className={'w-[120px] px-4 py-3 text-center font-medium'}>
                  {translate('featureFlags.columns.admins')}
                </th>
              </tr>
            </thead>

            <tbody>
              {sorted.map(flag => (
                <tr className={'border-b border-border last:border-b-0'} key={flag.key}>
                  <td className={'px-4 py-3 font-medium text-foreground'}>
                    {translate(`featureFlags.features.${flag.key}`)}
                  </td>

                  <td className={'px-4 py-3 text-center'}>
                    <div className={'flex justify-center'}>
                      <Checkbox
                        checked={flag.enabledForUsers}
                        disabled={updateFlag.isLoading}
                        onCheckedChange={value => toggle(flag, 'enabledForUsers', value === true)}
                      />
                    </div>
                  </td>

                  <td className={'px-4 py-3 text-center'}>
                    <div className={'flex justify-center'}>
                      <Checkbox
                        checked={flag.enabledForAdmins}
                        disabled={updateFlag.isLoading}
                        onCheckedChange={value => toggle(flag, 'enabledForAdmins', value === true)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
